import { useState, useMemo } from 'react'
import { format, parseISO } from 'date-fns'
import { useBudgetCategories, useTransactions, useBills } from './hooks/useBudget'
import { Spinner } from '../../components/ui/Spinner'
import { Button } from '../../components/ui/Button'
import { cn } from '../../lib/utils'
import { Portal } from '../../components/Portal'

const COLORS = ['slate','rose','amber','emerald','sky','violet','pink','orange','teal','indigo']
const COLOR_HEX = {
  slate:'#64748b', rose:'#f43f5e', amber:'#f59e0b', emerald:'#10b981',
  sky:'#0ea5e9', violet:'#8b5cf6', pink:'#ec4899', orange:'#f97316',
  teal:'#14b8a6', indigo:'#6366f1',
}
const today = () => new Date()

// ─── Small modal shell ────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <Portal>
  <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center pb-[env(safe-area-inset-bottom,0px)] sm:pb-0 overflow-hidden" onClick={onClose}>
    <div className="absolute inset-0 bg-black/40" />
    <div
      className="relative bg-surface rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[90dvh] flex flex-col shadow-card"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
        <h2 className="text-base font-bold text-text-primary">{title}</h2>
        <button onClick={onClose} className="text-text-muted p-1">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4">{children}</div>
    </div>
  </div>
  </Portal>
)

// ─── Category setup screen ────────────────────────────────────────────────────
const CategorySetup = ({ onDone }) => {
  const { createCategory } = useBudgetCategories()
  const [rows, setRows] = useState([
    { name: 'Salary', type: 'income', monthly_goal: '', color: 'emerald' },
    { name: 'Groceries', type: 'expense', monthly_goal: '', color: 'amber' },
    { name: 'Utilities', type: 'expense', monthly_goal: '', color: 'sky' },
  ])
  const [saving, setSaving] = useState(false)

  const addRow = (type) => setRows((r) => [...r, { name: '', type, monthly_goal: '', color: 'slate' }])

  const save = async () => {
    setSaving(true)
    try {
      for (const r of rows.filter((x) => x.name.trim())) {
        await createCategory({
          name:         r.name.trim(),
          type:         r.type,
          monthly_goal: r.monthly_goal ? parseFloat(r.monthly_goal) : null,
          color:        r.color,
        })
      }
      onDone()
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="px-4 py-6 max-w-xl mx-auto">
      <h1 className="text-xl font-bold text-text-primary mb-1">Set up your Budget</h1>
      <p className="text-sm text-text-muted mb-6">Add income sources and expense categories to get started.</p>

      {['income', 'expense'].map((type) => (
        <div key={type} className="mb-6">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3 capitalize">{type}</h2>
          <div className="space-y-2">
            {rows.filter((r) => r.type === type).map((row, i) => {
              const realIdx = rows.indexOf(row)
              return (
                <div key={i} className="flex gap-2 items-center bg-surface border border-border rounded-xl p-2">
                  <select
                    value={row.color}
                    onChange={(e) => setRows((prev) => prev.map((r, j) => j === realIdx ? { ...r, color: e.target.value } : r))}
                    className="text-xs rounded-lg border border-border px-2 py-1"
                  >
                    {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input
                    type="text"
                    placeholder="Category name"
                    value={row.name}
                    onChange={(e) => setRows((prev) => prev.map((r, j) => j === realIdx ? { ...r, name: e.target.value } : r))}
                    className="flex-1 text-sm border border-border rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <input
                    type="number"
                    placeholder="Monthly goal $"
                    value={row.monthly_goal}
                    onChange={(e) => setRows((prev) => prev.map((r, j) => j === realIdx ? { ...r, monthly_goal: e.target.value } : r))}
                    className="flex-1 min-w-0 w-0 text-sm border border-border rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button onClick={() => setRows((prev) => prev.filter((_, j) => j !== realIdx))} className="text-text-muted hover:text-rose-500">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
                  </button>
                </div>
              )
            })}
          </div>
          <button onClick={() => addRow(type)} className="mt-2 text-sm text-primary font-medium flex items-center gap-1">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
            Add {type} category
          </button>
        </div>
      ))}

      <Button onClick={save} disabled={saving} className="w-full mt-4">
        {saving ? <Spinner size="sm" /> : 'Start tracking'}
      </Button>
    </div>
  )
}

// ─── Add transaction form ─────────────────────────────────────────────────────
const AddTransactionModal = ({ categories, onAdd, onClose }) => {
  const [amount,      setAmount]      = useState('')
  const [categoryId,  setCategoryId]  = useState(categories[0]?.id ?? '')
  const [description, setDescription] = useState('')
  const [date,        setDate]        = useState(format(today(), 'yyyy-MM-dd'))
  const [saving,      setSaving]      = useState(false)
  const [error,       setError]       = useState('')

  const submit = async (e) => {
    e.preventDefault()
    if (!amount || !categoryId) return
    setSaving(true)
    setError('')
    try {
      await onAdd({ amount: parseFloat(amount), category_id: categoryId, description: description || null, date })
      onClose()
    } catch (err) {
      setError(err?.message ?? 'Could not save transaction. Please try again.')
    } finally { setSaving(false) }
  }

  return (
    <Modal title="Add Transaction" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1 block">Amount</label>
          <input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
        </div>
        <div>
          <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1 block">Category</label>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1 block">Description</label>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
        </div>
        <div>
          <label className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1 block">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
        </div>
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" className="flex-1" disabled={saving}>{saving ? <Spinner size="sm" /> : 'Add'}</Button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Add bill form ────────────────────────────────────────────────────────────
const AddBillModal = ({ onAdd, onClose }) => {
  const [name,      setName]      = useState('')
  const [amount,    setAmount]    = useState('')
  const [dueDay,    setDueDay]    = useState('1')
  const [frequency, setFrequency] = useState('monthly')
  const [saving,    setSaving]    = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await onAdd({ name, amount: parseFloat(amount), due_day: parseInt(dueDay), frequency })
      onClose()
    } finally { setSaving(false) }
  }

  return (
    <Modal title="Add Bill" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <input type="text" placeholder="Bill name" value={name} onChange={(e) => setName(e.target.value)} autoFocus
          className="w-full rounded-base border border-border px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
        <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-text-muted mb-1 block">Amount</label>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <label className="text-xs text-text-muted mb-1 block">Due day</label>
            <input type="number" min="1" max="31" value={dueDay} onChange={(e) => setDueDay(e.target.value)}
              className="w-full rounded-base border border-border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div>
            <label className="text-xs text-text-muted mb-1 block">Frequency</label>
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)}
              className="w-full rounded-base border border-border px-3 py-2 text-sm">
              {['monthly','weekly','yearly','one-time'].map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="secondary" size="sm" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" className="flex-1" disabled={saving || !name || !amount}>{saving ? <Spinner size="sm" /> : 'Add Bill'}</Button>
        </div>
      </form>
    </Modal>
  )
}

// ─── Overview tab ─────────────────────────────────────────────────────────────
const OverviewTab = ({ categories, transactions, year, month }) => {
  const [showAdd, setShowAdd] = useState(false)
  const { addTransaction, deleteTransaction } = useTransactions(year, month)

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories])

  const incomeCategories  = categories.filter((c) => c.type === 'income')
  const expenseCategories = categories.filter((c) => c.type === 'expense')

  const totalIncome = useMemo(() =>
    transactions.filter((t) => catMap[t.category_id]?.type === 'income').reduce((s, t) => s + Number(t.amount), 0),
    [transactions, catMap])

  const totalExpense = useMemo(() =>
    transactions.filter((t) => catMap[t.category_id]?.type === 'expense').reduce((s, t) => s + Number(t.amount), 0),
    [transactions, catMap])

  const spentByCategory = useMemo(() => {
    const map = {}
    for (const t of transactions) {
      if (!map[t.category_id]) map[t.category_id] = 0
      map[t.category_id] += Number(t.amount)
    }
    return map
  }, [transactions])

  return (
    <>
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { label: 'Income', amount: totalIncome, cls: 'text-emerald-600' },
          { label: 'Spent',  amount: totalExpense, cls: 'text-rose-500' },
          { label: 'Left',   amount: totalIncome - totalExpense, cls: totalIncome - totalExpense >= 0 ? 'text-primary' : 'text-rose-600' },
        ].map(({ label, amount, cls }) => (
          <div key={label} className="bg-surface rounded-xl border border-border p-3 text-center">
            <div className="text-xs text-text-muted">{label}</div>
            <div className={`text-base font-bold mt-0.5 ${cls}`}>
              ${Math.abs(amount).toFixed(2)}
            </div>
          </div>
        ))}
      </div>

      {/* Expense progress bars */}
      {expenseCategories.length > 0 && (
        <div className="mb-5 space-y-3">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wide">Spending</h2>
          {expenseCategories.map((cat) => {
            const spent = spentByCategory[cat.id] ?? 0
            const goal  = cat.monthly_goal ?? 0
            const pct   = goal > 0 ? Math.min((spent / goal) * 100, 100) : 0
            const over  = goal > 0 && spent > goal
            return (
              <div key={cat.id}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-medium text-text-primary">{cat.name}</span>
                  <span className={over ? 'text-rose-500 font-semibold' : 'text-text-muted'}>
                    ${spent.toFixed(2)}{goal ? ` / $${goal.toFixed(2)}` : ''}
                  </span>
                </div>
                {goal > 0 && (
                  <div className="h-2 bg-background rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${over ? 'bg-rose-400' : 'bg-primary'}`}
                      style={{ width: `${pct}%`, backgroundColor: over ? undefined : (COLOR_HEX[cat.color] ?? '#2d1b5e') }}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Transaction list */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wide">Transactions</h2>
        <button onClick={() => setShowAdd(true)} className="text-xs text-primary font-semibold flex items-center gap-1">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
          Add
        </button>
      </div>

      {transactions.length === 0 ? (
        <p className="text-sm text-text-muted text-center py-6">No transactions this month</p>
      ) : (
        <div className="space-y-1">
          {transactions.map((t) => {
            const cat = catMap[t.category_id]
            return (
              <div key={t.id} className="flex items-center gap-3 py-2.5 border-b border-border/50 last:border-0">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: COLOR_HEX[cat?.color] ?? '#7c6faf' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-text-primary">{t.description || cat?.name}</div>
                  <div className="text-xs text-text-muted">{format(parseISO(t.date), 'MMM d')}</div>
                </div>
                <div className={`text-sm font-semibold flex-shrink-0 ${cat?.type === 'income' ? 'text-emerald-600' : 'text-text-primary'}`}>
                  {cat?.type === 'income' ? '+' : '-'}${Number(t.amount).toFixed(2)}
                </div>
                <button onClick={() => deleteTransaction(t.id)} className="text-text-muted hover:text-rose-500 flex-shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6 6 18M6 6l12 12" /></svg>
                </button>
              </div>
            )
          })}
        </div>
      )}

      {showAdd && (
        <AddTransactionModal categories={categories} onAdd={addTransaction} onClose={() => setShowAdd(false)} />
      )}
    </>
  )
}

// ─── Bills tab ────────────────────────────────────────────────────────────────
const BillsTab = () => {
  const { data: bills = [], isLoading, createBill, togglePaid, deleteBill } = useBills()
  const [showAdd, setShowAdd] = useState(false)

  const thisMonth = today().getMonth() + 1
  const isPaidThisMonth = (bill) => {
    if (!bill.last_paid_at) return false
    const d = parseISO(bill.last_paid_at)
    return d.getMonth() + 1 === thisMonth && d.getFullYear() === today().getFullYear()
  }

  if (isLoading) return <div className="flex justify-center py-8"><Spinner /></div>

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wide">Recurring Bills</h2>
        <button onClick={() => setShowAdd(true)} className="text-xs text-primary font-semibold flex items-center gap-1">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
          Add Bill
        </button>
      </div>

      {bills.length === 0 ? (
        <p className="text-sm text-text-muted text-center py-8">No bills yet</p>
      ) : (
        <div className="space-y-2">
          {bills.map((bill) => {
            const paid = isPaidThisMonth(bill)
            return (
              <div key={bill.id} className={cn('flex items-center gap-3 bg-surface rounded-xl border p-3', paid ? 'border-emerald-200 opacity-70' : 'border-border')}>
                <button
                  onClick={() => togglePaid(bill)}
                  className={cn('w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0',
                    paid ? 'bg-emerald-400 border-emerald-400' : 'border-border')}
                >
                  {paid && <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5"><path d="m2 6 3 3 5-5" /></svg>}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text-primary">{bill.name}</div>
                  <div className="text-xs text-text-muted">Due day {bill.due_day} · {bill.frequency}</div>
                </div>
                <div className="text-sm font-bold text-text-primary">${Number(bill.amount).toFixed(2)}</div>
                <button onClick={() => deleteBill(bill.id)} className="text-text-muted hover:text-rose-500">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></svg>
                </button>
              </div>
            )
          })}
        </div>
      )}

      {showAdd && <AddBillModal onAdd={createBill} onClose={() => setShowAdd(false)} />}
    </>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────
export const BudgetPage = () => {
  const now = today()
  const [year,  setYear]  = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [tab,   setTab]   = useState('overview')

  const { data: categories = [], isLoading: catLoading } = useBudgetCategories()
  const { data: transactions = [], isLoading: txLoading } = useTransactions(year, month)

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1) }
    else setMonth((m) => m - 1)
  }
  const nextMonth = () => {
    const n = new Date(now.getFullYear(), now.getMonth())
    const c = new Date(year, month - 1)
    if (c >= n) return
    if (month === 12) { setMonth(1); setYear((y) => y + 1) }
    else setMonth((m) => m + 1)
  }
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1

  if (catLoading) return <div className="flex justify-center py-16"><Spinner /></div>
  if (categories.length === 0) return <CategorySetup onDone={() => {}} />

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">
      {/* Month selector */}
      <div className="flex items-center justify-between mb-5">
        <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-background">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <h1 className="text-base font-bold text-text-primary">
          {format(new Date(year, month - 1), 'MMMM yyyy')}
        </h1>
        <button onClick={nextMonth} disabled={isCurrentMonth} className="p-1.5 rounded-lg hover:bg-background disabled:opacity-30">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-background rounded-xl p-1">
        {['overview', 'bills'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={cn('flex-1 py-1.5 text-sm font-medium rounded-lg capitalize transition-colors',
              tab === t ? 'bg-surface text-primary shadow-sm' : 'text-text-muted')}>
            {t}
          </button>
        ))}
      </div>

      {tab === 'overview' && !txLoading && (
        <OverviewTab categories={categories} transactions={transactions} year={year} month={month} />
      )}
      {tab === 'overview' && txLoading && <div className="flex justify-center py-8"><Spinner /></div>}
      {tab === 'bills' && <BillsTab />}
    </div>
  )
}
